import { api, type ApiResponse } from '../client';

export enum DonationRequestStatusEnum {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface DonationRequest {
  uuid: string;
  user_uuid: string;
  charity_uuid: string;
  employer_uuid: string;
  leave_hours: number;
  leave_value: number;
  donation_request_status_uuid: string;
  created_at: string;
  updated_at: string;
  rejection_reason?: string;
  rejected_at?: string;
  employer_comment?: string;
  employer_top_up?: number;
  has_authorisation_pdf?: boolean;
  /** Canonical lifecycle stage from the backend (App\Support\DonationLifecycle) */
  lifecycle_stage?: string;
  // Extended properties from API response
  user?: {
    uuid: string;
    full_name: string;
    email: string;
    department?: string;
    location?: string
  };
  charity?: {
    uuid: string;
    name: string;
    logo?: string;
  };
  donation_request_status?: {
    uuid: string;
    name: string;
  };
}

export interface DonationRequestFilters {
  donation_request_status_uuid?: string;
  lifecycle_stage?: string;
  user_uuid?: string;
  charity_uuid?: string;
  phrase?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: 'created_at' | 'updated_at' | 'leave_hours' | 'leave_value';
  sort_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export interface DonationRequestResponse {
  success: boolean;
  message?: string;
  /** List of donation requests (new API shape) or legacy Laravel paginator object */
  data: DonationRequest[] | {
    current_page: number;
    data: DonationRequest[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: { url: string | null; label: string; active: boolean }[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
  paging?: {
    current_page: number;
    last_page: number;
    per_page: number;
    current_items: number;
    from: number;
    to: number;
    total: number;
  };
}

// API base URL
const API_BASE_URL = '/donation-request/employer';

export class EmployerLeaveRequestService {
  private static instance: EmployerLeaveRequestService;

  private constructor() {}

  public static getInstance(): EmployerLeaveRequestService {
    if (!EmployerLeaveRequestService.instance) {
      EmployerLeaveRequestService.instance = new EmployerLeaveRequestService();
    }
    return EmployerLeaveRequestService.instance;
  }

  /**
   * Get all donation requests for the authenticated employer
   * @param filters Optional filters for the request
   * @returns Promise with the donation requests data
   */
  public async getLeaveRequests(filters?: DonationRequestFilters): Promise<DonationRequestResponse> {
    try {
      const response = await api.get<DonationRequestResponse>(API_BASE_URL, {
        params: filters
      });
      const raw = response.data as { success: boolean; data: { items?: DonationRequest[]; paging?: unknown } | DonationRequest[]; paging?: unknown };
      const d = raw.data;
      const items = (d && typeof d === 'object' && 'items' in d ? d.items : Array.isArray(d) ? d : []) ?? [];
      const paging = (d && typeof d === 'object' && 'paging' in d ? d.paging : raw.paging) ?? undefined;
      return { ...raw, data: items, paging } as DonationRequestResponse;
    } catch (error) {
      console.error('Error fetching employer leave requests:', error);
      throw error;
    }
  }

  /**
   * Get a specific donation request by UUID
   * @param uuid The UUID of the donation request
   * @returns Promise with the donation request data
   */
  public async getLeaveRequestDetail(uuid: string): Promise<DonationRequest> {
    try {
      const response = await api.get<ApiResponse<DonationRequest>>(`/donation-request/${uuid}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employer leave request detail:', error);
      throw error;
    }
  }

  /**
   * Approve a donation request
   * @param uuid The UUID of the donation request to approve
   * @param data Approval data including leave value and optional top-up
   * @returns Promise with the updated donation request
   */
  public async approveLeaveRequest(
    uuid: string, 
    data: {
      leave_value_cents: number;
      employer_top_up_cents?: number;
      employer_comment?: string;
      terms_accepted: boolean;
    }
  ): Promise<DonationRequest> {
    try {
      const response = await api.put<ApiResponse<DonationRequest>>(
        `/donation-request/${uuid}/approve`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  }

  /**
   * Reject a donation request
   * @param uuid The UUID of the donation request to reject
   * @param reason Required reason for rejection
   * @returns Promise with the rejection response
   */
  public async rejectLeaveRequest(uuid: string, reason: string): Promise<{ donationId: string; reason: string }> {
    try {
      const response = await api.put<ApiResponse<{ donationId: string; reason: string }>>(
        `/donation-request/${uuid}/reject`,
        { reason }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  }

  /**
   * Download the Leave Cashout Authorisation PDF for an approved request.
   * @param uuid Donation request UUID
   * @param filename Optional filename for the download
   */
  public async downloadAuthorisationPdf(uuid: string, filename = 'Leave-Cashout-Authorisation.pdf'): Promise<void> {
    const response = await api.get<Blob>(`/donation-request/${uuid}/authorisation-pdf`, {
      responseType: 'blob',
    });
    const blob = response.data as unknown as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}



export default EmployerLeaveRequestService.getInstance(); 